"use client";

import { Button } from "@/components/ui/button";
import ImageSvg from "@/components/ui/image-svg";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/contexts/auth-context";
import { Eye, EyeOff } from "lucide-react";
import { Component, type ReactNode, useState } from "react";
import ReCAPTCHA from "react-google-recaptcha";
import { Link, useLocation, useNavigate } from "react-router-dom";

class RecaptchaErrorBoundary extends Component<
  { children: ReactNode },
  { hasError: boolean }
> {
  state = { hasError: false };
  static getDerivedStateFromError() {
    return { hasError: true };
  }
  render() {
    if (this.state.hasError) return null;
    return this.props.children;
  }
}

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const { signIn } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const from = location.state?.from?.pathname || "/cliente";

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    try {
      await signIn(email, password);

      navigate(from, { replace: true });
    } catch {
      setError("E-mail ou senha inválidos");
    }
  };

  return (
    <div className="min-h-screen px-5 py-10 md:px-10 md:py-20">
      <div className="flex flex-col md:flex-row items-center justify-center gap-10">
        <div className="bg-white max-w-[600px] rounded-[20px] w-full md:w-1/2 px-10 py-12">
          <div className="max-w-xl mx-auto space-y-8">
            <div className="text-center">
              <h1 className="text-4xl md:text-5xl font-bold text-n-text-green">
                Login
              </h1>
              <p className="text-md md:text-lg text-n-text-neutral mt-2">
                Seja bem-vindo de volta!
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5">
              <Input
                type="email"
                placeholder="Email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />

              <div className="relative">
                <Input
                  type={showPassword ? "text" : "password"}
                  placeholder="Senha"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2"
                >
                  {showPassword ? (
                    <EyeOff className="w-4 h-4 text-n-text" />
                  ) : (
                    <Eye className="w-4 h-4 text-n-text" />
                  )}
                </button>
              </div>

              {!captchaToken && import.meta.env.VITE_RECAPTCHA_SITE_KEY && (
                <div className="pt-2">
                  <RecaptchaErrorBoundary>
                    <ReCAPTCHA
                      sitekey={import.meta.env.VITE_RECAPTCHA_SITE_KEY}
                      onChange={(token: string | null) => setCaptchaToken(token)}
                      onExpired={() => setCaptchaToken(null)}
                    />
                  </RecaptchaErrorBoundary>
                </div>
              )}

              {error && (
                <p className="text-sm text-n-text-red text-center">{error}</p>
              )}
              <Button type="submit" className="w-full text-lg">
                Login
              </Button>

              <div className="flex flex-col items-center justify-between text-sm border-t pt-10 gap-4">
                <Link
                  to="/esqueci-senha"
                  className="text-n-primary underline hover:text-n-primary/80"
                >
                  Esqueceu a sua senha?
                </Link>
                {/* Link para criar conta comentado */}
                {/* <Link to="/cadastro" className="text-n-text ">
                  Não possui uma conta?{" "}
                  <span className="underline hover:text-n-primary/80">
                    Criar conta
                  </span>
                </Link> */}
              </div>
            </form>
          </div>
        </div>

        {/* Ilustração (mesma das outras telas) */}
        <div className="w-full md:w-1/2 flex items-center justify-center">
          <ImageSvg
            src={"concept-company.svg"}
            alt="Login"
            className="w-[320px] md:w-[600px] max-w-full"
          />
        </div>
      </div>
    </div>
  );
}
